// Requests & Hooks für Sponsor-Agreements eines Sponsors

import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import type {
  SponsorAgreementResponseDto,
  CreateSponsorAgreementRequestDto,
  UpdateSponsorAgreementRequestDto,
  SponsorAgreementAdvertisementResponseDto,
  AgreementDocumentUrlResponseDto,
} from "@/lib/api/interfaces/sponsor-agreements.interface";
import { sponsorsKeys } from "./sponsors.requests";

export const sponsorAgreementsKeys = {
  all: ["sponsor-agreements"] as const,
  list: (sponsorId: string) =>
    ([...sponsorAgreementsKeys.all, "list", sponsorId] as const),
  advertisements: (sponsorId: string) =>
    ([...sponsorAgreementsKeys.all, "advertisements", sponsorId] as const),
} as const;

/**
 * Agreements eines Sponsors laden
 */
export function useSponsorAgreementsQuery(
  sponsorId: string,
  options?: Omit<
    UseQueryOptions<SponsorAgreementResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: sponsorAgreementsKeys.list(sponsorId),
    // Normalisiere die API-Antwort: die API kann ein Array oder ein Objekt (z.B. paginierte Antwort)
    // zurückgeben. Wir wandeln das Ergebnis hier in ein Array von SponsorAgreementResponseDto um,
    // damit Komponenten sich darauf verlassen können, immer ein Array zu erhalten.
    queryFn: async () => {
      const res = await apiRequest<any>(`/sponsors/${sponsorId}/agreements`);
      if (!res) return [] as SponsorAgreementResponseDto[];
      if (Array.isArray(res)) return res as SponsorAgreementResponseDto[];
      if (Array.isArray((res as any).docs)) return (res as any).docs as SponsorAgreementResponseDto[];
      if (Array.isArray((res as any).items)) return (res as any).items as SponsorAgreementResponseDto[];
      if (Array.isArray((res as any).data)) return (res as any).data as SponsorAgreementResponseDto[];
      // Fallback: falls das Objekt ein einzelnes Agreement ist, gib ein Array mit dem Eintrag zurück
      if (res && typeof res === "object" && (res as any)._id)
        return [res as SponsorAgreementResponseDto];
      return [] as SponsorAgreementResponseDto[];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Werbemittel-/Leistungs-Definitionen (Advertisements) für Agreements eines Sponsors laden
 */
export function useSponsorAgreementAdvertisementsQuery(
  sponsorId: string,
  options?: Omit<
    UseQueryOptions<SponsorAgreementAdvertisementResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: sponsorAgreementsKeys.advertisements(sponsorId),
    queryFn: async () => {
      const res = await apiRequest<SponsorAgreementAdvertisementResponseDto[]>(
        `/sponsors/${sponsorId}/agreements/advertisements`,
      );
      // Backend gibt bereits ein Array zurück; falls nicht, normalisieren wir defensiv
      if (!res) return [] as SponsorAgreementAdvertisementResponseDto[];
      if (Array.isArray(res)) return res;
      const anyRes = res as any;
      if (Array.isArray(anyRes.data)) return anyRes.data as SponsorAgreementAdvertisementResponseDto[];
      if (Array.isArray(anyRes.items)) return anyRes.items as SponsorAgreementAdvertisementResponseDto[];
      if (Array.isArray(anyRes.docs)) return anyRes.docs as SponsorAgreementAdvertisementResponseDto[];
      return [] as SponsorAgreementAdvertisementResponseDto[];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Neues Agreement für einen Sponsor anlegen
 */
export function useSponsorAgreementCreate(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<
      SponsorAgreementResponseDto,
      Error,
      CreateSponsorAgreementRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateSponsorAgreementRequestDto) =>
      apiRequest<SponsorAgreementResponseDto>(
        `/sponsors/${sponsorId}/agreements`,
        "POST",
        data,
      ),
    onSuccess: (data, variables, context) => {
      // Agreements-Liste des Sponsors neu laden
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          return (
            Array.isArray(key) &&
            key[0] === "sponsor-agreements" &&
            key.includes("list") &&
            key.includes(sponsorId)
          );
        },
      });
      // Optionale Aktualisierung von Sponsor-Listen
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export interface UploadAgreementDocumentsVars {
  agreementId: string;
  files: File[];
}

/**
 * Dokumente (PDF/Bilder) zu einem Agreement hochladen
 */
export function useSponsorAgreementDocumentsUpload(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<
      SponsorAgreementResponseDto,
      Error,
      UploadAgreementDocumentsVars
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ agreementId, files }: UploadAgreementDocumentsVars) => {
      const form = new FormData();
      (files || []).forEach((f) => form.append("files", f));
      return await apiRequest<SponsorAgreementResponseDto>(
        `/sponsors/${sponsorId}/agreements/${agreementId}/documents`,
        "POST",
        form,
      );
    },
    onSuccess: (data, variables, context) => {
      // Agreements des Sponsors neu laden
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          return (
            Array.isArray(key) &&
            key[0] === "sponsor-agreements" &&
            key.includes("list") &&
            key.includes(sponsorId)
          );
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export interface AgreementDocumentUrlVars {
  agreementId: string;
  docIndex: number;
}

/**
 * Signierte URL zum Anzeigen eines Agreement-Dokuments abrufen
 */
export function useSponsorAgreementDocumentUrl(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<AgreementDocumentUrlResponseDto, Error, AgreementDocumentUrlVars>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: async ({ agreementId, docIndex }: AgreementDocumentUrlVars) => {
      return await apiRequest<AgreementDocumentUrlResponseDto>(
        `/sponsors/${sponsorId}/agreements/${agreementId}/documents/${docIndex}/url`,
      );
    },
  });
}

export interface UpdateSponsorAgreementVars {
  agreementId: string;
  data: UpdateSponsorAgreementRequestDto;
}

/**
 * Bestehendes Agreement eines Sponsors aktualisieren
 */
export function useSponsorAgreementUpdate(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<
      SponsorAgreementResponseDto,
      Error,
      UpdateSponsorAgreementVars
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ agreementId, data }: UpdateSponsorAgreementVars) =>
      apiRequest<SponsorAgreementResponseDto>(
        `/sponsors/${sponsorId}/agreements/${agreementId}`,
        "PATCH",
        data,
      ),
    onSuccess: (data, variables, context) => {
      // Agreements-Liste des Sponsors neu laden
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          return (
            Array.isArray(key) &&
            key[0] === "sponsor-agreements" &&
            key.includes("list") &&
            key.includes(sponsorId)
          );
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete agreement for sponsor
 */
export function useSponsorAgreementDelete(
  sponsorId: string,
  options?: Omit<UseMutationOptions<void, Error, { agreementId: string }>, "mutationFn">
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ agreementId }: { agreementId: string }) =>
      apiRequest<void>(`/sponsors/${sponsorId}/agreements/${agreementId}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      // Agreements-Liste des Sponsors neu laden
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          return (
            Array.isArray(key) &&
            key[0] === "sponsor-agreements" &&
            key.includes("list") &&
            key.includes(sponsorId)
          );
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
